import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, ArrowRight } from 'lucide-react';
import { businessInfo } from '../mockData';
import { siteConfig } from '../content/siteContent';
import { Button } from './ui/button';

const quickLinks = [
  { to: '/', label: 'Home' },
  { to: '/residential', label: 'Residential' },
  { to: '/commercial', label: 'Commercial' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/partners', label: 'Trade Partners' },
  { to: '/service-areas', label: 'Service Areas' },
  { to: '/blog', label: 'Tips & Guides' },
  { to: '/projects', label: 'Projects' },
  { to: '/reviews', label: 'Reviews' },
  { to: '/about', label: 'About Us' },
  { to: '/contact', label: 'Contact' },
];

const serviceLinks = [
  { to: '/residential/lock-repair-replacement', label: 'Lock Repair & Replacement' },
  { to: '/residential/home-security-upgrades', label: 'Home Security Upgrades' },
  { to: '/commercial/door-hardware-installation', label: 'Commercial Door Hardware' },
  { to: '/commercial/access-control-installation', label: 'Access Control Installation' },
  { to: '/commercial/maintenance-iqp', label: 'Maintenance & IQP Support' },
];

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* CTA Section */}
      <div className="bg-red-600 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
            Locks sorted — home, business or site
          </h2>
          <p className="text-lg sm:text-xl text-red-100 mb-8 max-w-3xl mx-auto">
            Mobile locksmiths and door hardware specialists. Fixed quotes before any extra work starts.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${businessInfo.phoneTel}`}>
              <Button size="lg" className="bg-white text-red-700 hover:bg-red-50 font-bold">
                <Phone className="mr-2" size={20} />
                Call {businessInfo.phone}
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-semibold">
                Request a quote
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info with Logo */}
          <div className="lg:col-span-1">
            <div style={{ flexShrink: 0, overflow: 'visible', marginBottom: '24px', paddingTop: '4px', paddingBottom: '4px' }} data-testid="footer-logo-container">
              <Link to="/">
                <img
                  src="/logo.png"
                  alt="Shore Doors Locksmiths"
                  data-testid="footer-logo-img"
                  style={{ height: '56px', width: 'auto', display: 'block' }}
                />
              </Link>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed text-sm">
              {siteConfig.brandName} — mobile locksmiths and commercial door hardware installers.
              The installation partner Auckland's security companies trust, now serving homes and
              businesses directly.
            </p>
            <div className="flex items-start gap-3 text-gray-400 mb-3">
              <MapPin size={18} className="mt-1 flex-shrink-0" />
              <span>{businessInfo.address}</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-gray-400 hover:text-white transition-colors inline-block">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Our Services</h3>
            <ul className="space-y-3">
              {serviceLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-gray-400 hover:text-white transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Contact Us</h3>
            <div className="space-y-4">
              <a href={`tel:${businessInfo.phoneTel}`} className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors">
                <Phone size={18} />
                <span>{businessInfo.phone}</span>
              </a>
              <a href={`mailto:${businessInfo.email}`} className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors">
                <Mail size={18} />
                <span className="break-all">{businessInfo.email}</span>
              </a>
              <div className="flex items-start gap-3 text-gray-400">
                <Clock size={18} className="mt-1 flex-shrink-0" />
                <div>
                  <p>{siteConfig.hoursLine}</p>
                  <p className="text-sm text-gray-500">{siteConfig.afterHoursLine}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400 text-sm">
          <p>&copy; {currentYear} Shore Doors. All rights reserved.</p>
          <p className="mt-2">{siteConfig.brandName} — Mobile Locksmiths & Commercial Door Hardware, Auckland</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
